import React from 'react'
import { cn, formatCurrency } from '@/lib/utils'

// Always-visible cross-testing feedback for the new-comparability designer
// (Phase 5, plan Part A2). Reads the LIVE metrics.crossTesting object the store
// recomputes on every recalc, so gateway / rate-group / ABPT / EBAR results
// update as the design is typed — no "Solve" gate. Copy stays modeled and
// humble: this is a screen on documented assumptions, not a formal
// nondiscrimination determination. When the gateway fails, a passing rate-group
// conclusion is never rendered (gateway-not-met dominates, per §401(a)(4)).

const fmtPct = (value) => (value === null || value === undefined ? '—' : `${Number(value).toFixed(2)}%`)

function TestBadge({ ok, muted, children }) {
  return (
    <span className={cn(
      'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium',
      muted ? 'bg-muted text-muted-foreground'
        : ok ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
          : 'bg-red-500/15 text-red-700 dark:text-red-300',
    )}>
      {muted ? '•' : ok ? '✓' : '✗'} {children}
    </span>
  )
}

export default function CrossTestingStrip({ crossTesting, employees = [], className }) {
  const nameById = React.useMemo(
    () => Object.fromEntries((employees || []).map((employee) => [employee.id, employee.name || employee.id])),
    [employees])

  if (!crossTesting || crossTesting.available === false) {
    return (
      <p className={cn('text-xs text-muted-foreground', className)}>
        Add at least one owner or HCE and a nonelective allocation to see live cross-testing
        (gateway, rate groups, ABPT).
      </p>
    )
  }

  const gatewayFails = crossTesting.gatewaySatisfied === false
  const rateGroups = crossTesting.rateGroups || []
  const passingRateGroups = rateGroups.filter((group) => group.passes).length
  const rows = crossTesting.rows || []

  return (
    <div className={cn('space-y-2', className)} data-testid="cross-testing-strip">
      <div className="flex flex-wrap items-center gap-1.5">
        <TestBadge ok={crossTesting.gatewaySatisfied}>
          Gateway minimum {fmtPct(crossTesting.gatewayRate)}
        </TestBadge>
        <TestBadge ok={crossTesting.allRateGroupsPass} muted={gatewayFails}>
          Rate groups {rateGroups.length ? `${passingRateGroups}/${rateGroups.length}` : ''}
        </TestBadge>
        <TestBadge ok={crossTesting.abptPass}>ABPT {fmtPct(crossTesting.abptRatio)}</TestBadge>
      </div>
      <p className="text-xs text-muted-foreground">
        Owner avg EBAR {fmtPct(crossTesting.hceAvgEbar)} {'·'} Staff avg EBAR {fmtPct(crossTesting.nhceAvgEbar)}.
        {gatewayFails && crossTesting.gatewayShortfallCount > 0 && (
          <>{' '}Gateway short for {crossTesting.gatewayShortfallCount}{' '}
            {crossTesting.gatewayShortfallCount === 1 ? 'person' : 'people'} (about{' '}
            {formatCurrency(crossTesting.gatewayShortfallTotal || 0)} to close).</>
        )}
      </p>
      <p className="text-[11px] text-muted-foreground">
        A modeled §401(a)(4) screen, not a formal test. Confirm with the plan&rsquo;s TPA.
      </p>
      {rows.length > 0 && (
        <details className="rounded-md border border-dashed p-2 text-xs text-muted-foreground">
          <summary className="cursor-pointer font-medium text-foreground">
            Per-person detail (EBAR worksheet)
          </summary>
          <div className="mt-1.5 max-h-64 overflow-auto rounded-md border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-card text-muted-foreground">
                <tr className="border-b">
                  <th className="p-2 text-left font-medium">Employee</th>
                  <th className="p-2 text-left font-medium">Group</th>
                  <th className="p-2 text-right font-medium">Compensation</th>
                  <th className="p-2 text-right font-medium">Allocation %</th>
                  <th className="p-2 text-right font-medium">Testing age</th>
                  <th className="p-2 text-right font-medium">EBAR</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b last:border-0">
                    <td className="p-2 text-foreground">{nameById[row.id] || row.id}</td>
                    <td className="p-2">{row.isHCE ? 'HCE / Owner' : 'Staff (NHCE)'}</td>
                    <td className="p-2 text-right tabular-nums">{formatCurrency(row.comp || 0)}</td>
                    <td className="p-2 text-right tabular-nums">{fmtPct(row.allocationRate)}</td>
                    <td className="p-2 text-right tabular-nums">{row.testingAge ?? '—'}</td>
                    <td className="p-2 text-right tabular-nums">{fmtPct(row.ebar)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(crossTesting.modeledElections?.length > 0 || crossTesting.limitations?.length > 0) && (
            <div className="mt-2 space-y-1">
              {crossTesting.modeledElections?.map((election) => (
                <p key={election.election}>Modeled election: {election.election} ({election.citation}).</p>
              ))}
              {crossTesting.limitations?.map((line) => <p key={line}>Not evaluated: {line}</p>)}
            </div>
          )}
          <p className="mt-1">
            Testing age {crossTesting.testingAge}, interest {crossTesting.interest}%
            {crossTesting.assumptionSet ? `, assumptions ${crossTesting.assumptionSet}` : ''}.
          </p>
        </details>
      )}
    </div>
  )
}
